import { useState, useCallback, useMemo } from 'react';
import {
  Sparkles,
  Loader2,
  RefreshCw,
  Check,
  ChevronDown,
  ChevronRight,
  X,
  Download,
  Maximize2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ImageModel, ImageProviderInfo } from '@/lib/api';
import {
  useImageGeneration,
  IMAGE_SIZE_OPTIONS,
  STYLE_CHIP_CATEGORIES,
  GeneratedImageResult,
  EnhancedPromptResult,
} from '../hooks';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_PROMPT_LENGTH = 1000;

/** Primary aspect ratios shown in the visual grid */
const PRIMARY_RATIOS = [
  { value: '1:1', label: '1:1', hint: 'Instagram, Facebook' },
  { value: '16:9', label: '16:9', hint: 'Twitter/X, YouTube' },
  { value: '9:16', label: '9:16', hint: 'Stories, Reels, TikTok' },
  { value: '4:3', label: '4:3', hint: 'Facebook, general' },
];

/** Secondary ratios available via overflow */
const SECONDARY_RATIOS = IMAGE_SIZE_OPTIONS.filter(
  (opt) => !PRIMARY_RATIOS.some((p) => p.value === opt.value),
);

const VARIANT_OPTIONS = [1, 2, 3, 4] as const;

const QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'hd', label: 'HD' },
  { value: 'ultra-hd', label: 'Ultra HD' },
];

const COLOR_MOOD_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'warm', label: 'Warm' },
  { value: 'cool', label: 'Cool' },
  { value: 'desaturated', label: 'Desaturated' },
  { value: 'vivid', label: 'Vivid' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface ImageGenerateTabProps {
  /** Called when user confirms a generated image to attach */
  onImageConfirmed: (result: GeneratedImageResult) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function ImageGenerateTab({ onImageConfirmed }: ImageGenerateTabProps) {
  const {
    stage,
    progressMessage,
    error,
    results,
    history,
    models,
    providers,
    modelsLoading,
    variantProgress,
    isEnhancing,
    generate,
    enhancePrompt,
    cancel,
    reset,
  } = useImageGeneration();

  // ─── Form state ──────────────────────────────────────────────────────
  const [prompt, setPrompt] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState<EnhancedPromptResult | null>(null);
  const [showEnhanced, setShowEnhanced] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [showMoreRatios, setShowMoreRatios] = useState(false);
  const [variantCount, setVariantCount] = useState(1);
  const [styleText, setStyleText] = useState('');
  const [selectedChips, setSelectedChips] = useState<Set<string>>(new Set());
  const [showAllChips, setShowAllChips] = useState(false);

  // Model selection — default to Nano Banana (gemini-2.5-flash-image)
  const [selectedModelId, setSelectedModelId] = useState('gemini-2.5-flash-image');

  // Advanced settings
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [quality, setQuality] = useState('standard');
  const [colorMood, setColorMood] = useState('auto');
  const [seed, setSeed] = useState('');
  const [rawMode, setRawMode] = useState(false);

  // Reference image
  const [refOpen, setRefOpen] = useState(false);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [referencePreview, setReferencePreview] = useState<string | null>(null);
  const [styleStrength, setStyleStrength] = useState(50);
  const [contentMatch, setContentMatch] = useState(30);

  // Results selection
  const [selectedResults, setSelectedResults] = useState<Set<string>>(new Set());
  const [lightboxImage, setLightboxImage] = useState<GeneratedImageResult | null>(null);

  const isGenerating = stage === 'starting' || stage === 'generating' || stage === 'storing';

  // Determine if the selected model supports reference images
  const supportsReferenceImage = useMemo(() => {
    if (selectedModelId === 'auto') return true; // auto picks Nano Banana which supports it
    return models.find((m) => m.id === selectedModelId)?.supportsReferenceImage ?? false;
  }, [selectedModelId, models]);

  // ─── Style chip handling ─────────────────────────────────────────────
  const toggleChip = useCallback((chip: string) => {
    setSelectedChips((prev) => {
      const next = new Set(prev);
      if (next.has(chip)) {
        next.delete(chip);
      } else {
        next.add(chip);
      }
      // Update the style text field
      setStyleText(Array.from(next).join(', '));
      return next;
    });
  }, []);

  // Build combined style text from chips + manual input
  const effectiveStyleText = useMemo(() => {
    const chipText = Array.from(selectedChips).join(', ');
    if (chipText && styleText !== chipText) {
      return styleText; // User has manually edited
    }
    return chipText || styleText;
  }, [selectedChips, styleText]);

  // ─── Enhance prompt ──────────────────────────────────────────────────
  const handleEnhancePrompt = useCallback(async () => {
    if (!prompt.trim() || isEnhancing) return;
    try {
      const result = await enhancePrompt(prompt.trim());
      setEnhancedPrompt(result);
      setShowEnhanced(true);
    } catch {
      // Enhancement is optional — user can proceed with original prompt
    }
  }, [prompt, isEnhancing, enhancePrompt]);

  // ─── Generate ────────────────────────────────────────────────────────
  const handleGenerate = useCallback(() => {
    if (!prompt.trim()) return;

    // Resolve selected model (auto = let backend decide)
    const selectedModel =
      selectedModelId !== 'auto' ? models.find((m) => m.id === selectedModelId) : undefined;

    // Pass enhanced fields if present and visible
    const enhancedFields =
      enhancedPrompt && showEnhanced
        ? enhancedPrompt.fields.filter((f) => f.value.trim())
        : undefined;

    generate({
      prompt: prompt.trim(),
      enhancedFields,
      styleText: effectiveStyleText,
      size: aspectRatio,
      variantCount,
      model: selectedModel?.id,
      provider: selectedModel?.provider,
      quality: quality as 'standard' | 'hd' | 'ultra-hd',
      negativePrompt: negativePrompt.trim() || undefined,
      colorMood: colorMood !== 'auto' ? colorMood : undefined,
      seed: seed ? Number(seed) : undefined,
      rawMode,
      referenceStrength: referenceFile ? styleStrength : undefined,
    });
  }, [
    prompt,
    enhancedPrompt,
    showEnhanced,
    effectiveStyleText,
    aspectRatio,
    variantCount,
    selectedModelId,
    models,
    quality,
    negativePrompt,
    colorMood,
    seed,
    rawMode,
    referenceFile,
    styleStrength,
    generate,
  ]);

  // ─── Reference image handling ────────────────────────────────────────
  const handleReferenceUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReferenceFile(file);
    const url = URL.createObjectURL(file);
    setReferencePreview(url);
  }, []);

  const clearReference = useCallback(() => {
    if (referencePreview) URL.revokeObjectURL(referencePreview);
    setReferenceFile(null);
    setReferencePreview(null);
  }, [referencePreview]);

  // ─── Results selection ───────────────────────────────────────────────
  const toggleResultSelection = useCallback((requestId: string) => {
    setSelectedResults((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  }, []);

  const selectAllResults = useCallback(() => {
    setSelectedResults(new Set(results.map((r) => r.requestId)));
  }, [results]);

  const deselectAllResults = useCallback(() => {
    setSelectedResults(new Set());
  }, []);

  const handleAddToPost = useCallback(() => {
    const selected = results.filter((r) => selectedResults.has(r.requestId));
    for (const img of selected) {
      onImageConfirmed(img);
    }
  }, [results, selectedResults, onImageConfirmed]);

  const handleRegenerateAll = useCallback(() => {
    handleGenerate();
  }, [handleGenerate]);

  // ─── Generating state ────────────────────────────────────────────────
  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="relative">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <Sparkles className="h-5 w-5 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="text-center space-y-1">
          <p className="font-medium">{progressMessage || 'Generating...'}</p>
          {variantProgress && (
            <p className="text-sm text-muted-foreground">
              Variant {variantProgress.current} of {variantProgress.total}
            </p>
          )}
          <p className="text-sm text-muted-foreground">This usually takes 10-30 seconds</p>
        </div>
        <Button variant="outline" onClick={cancel} size="sm">
          Cancel
        </Button>
      </div>
    );
  }

  // ─── Results view (post-generation) ──────────────────────────────────
  if (stage === 'complete' && results.length > 0) {
    return (
      <div className="flex flex-col h-full gap-3 p-1">
        {/* Results grid */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className={cn('grid gap-3', results.length === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
            {results.map((img) => (
              <div
                key={img.requestId}
                className={cn(
                  'relative group rounded-lg overflow-hidden border-2 transition-colors cursor-pointer',
                  selectedResults.has(img.requestId)
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:border-muted-foreground/30',
                )}
                onClick={() => toggleResultSelection(img.requestId)}
              >
                <img
                  src={img.imageUrl}
                  alt={img.prompt}
                  className="w-full aspect-square object-cover"
                />

                {/* Selection checkbox */}
                <div className="absolute top-2 right-2">
                  <div
                    className={cn(
                      'h-6 w-6 rounded-full flex items-center justify-center transition-colors',
                      selectedResults.has(img.requestId)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background/80 text-muted-foreground opacity-0 group-hover:opacity-100',
                    )}
                  >
                    {selectedResults.has(img.requestId) && <Check className="h-4 w-4" />}
                  </div>
                </div>

                {/* Model badge */}
                {img.model && (
                  <Badge
                    variant="secondary"
                    className="absolute bottom-2 left-2 text-[10px] bg-background/80 backdrop-blur-sm"
                  >
                    {img.model}
                  </Badge>
                )}

                {/* Generation time */}
                {img.generationTimeMs != null && (
                  <span className="absolute bottom-2 right-2 text-[10px] text-muted-foreground bg-background/80 backdrop-blur-sm px-1.5 py-0.5 rounded">
                    {(img.generationTimeMs / 1000).toFixed(1)}s
                  </span>
                )}

                {/* Hover actions */}
                <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    className="h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(img);
                    }}
                    title="View full size"
                  >
                    <Maximize2 className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={img.imageUrl}
                    download={`generated-${img.requestId}.png`}
                    className="h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background text-muted-foreground hover:text-foreground"
                    onClick={(e) => e.stopPropagation()}
                    title="Download"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Error notice */}
        {error && (
          <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>
        )}

        {/* Bulk actions */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <div className="flex gap-2">
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={selectAllResults}
              >
                Select all
              </button>
              <span className="text-muted-foreground">|</span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={deselectAllResults}
              >
                Deselect all
              </button>
            </div>
            <span className="text-muted-foreground">
              {selectedResults.size} of {results.length} selected
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleAddToPost}
              disabled={selectedResults.size === 0}
              className="flex-1"
            >
              <Check className="h-4 w-4 mr-2" />
              Add to post
              {selectedResults.size > 0 && ` (${selectedResults.size})`}
            </Button>
            <Button variant="outline" onClick={handleRegenerateAll} title="Regenerate all">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Regenerate
            </Button>
            <Button variant="ghost" onClick={reset} title="Back to editor">
              Edit
            </Button>
          </div>
        </div>

        {/* Lightbox */}
        {lightboxImage && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
            onClick={() => setLightboxImage(null)}
          >
            <button
              type="button"
              className="absolute top-4 right-4 text-white hover:text-white/80"
              onClick={() => setLightboxImage(null)}
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={lightboxImage.imageUrl}
              alt={lightboxImage.prompt}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  // ─── Input form (idle / error) ───────────────────────────────────────
  return (
    <div className="flex flex-col h-full gap-4 p-1 overflow-y-auto">
      {/* Section 1: Prompt Input */}
      <PromptSection
        prompt={prompt}
        onPromptChange={setPrompt}
        enhancedPrompt={enhancedPrompt}
        showEnhanced={showEnhanced}
        onToggleEnhanced={setShowEnhanced}
        onEnhancedChange={setEnhancedPrompt}
        onEnhance={handleEnhancePrompt}
        isEnhancing={isEnhancing}
      />

      {/* Section 2: Aspect Ratio + Variant Count */}
      <div className="grid grid-cols-2 gap-4">
        <AspectRatioSection
          value={aspectRatio}
          onChange={setAspectRatio}
          showMore={showMoreRatios}
          onToggleMore={() => setShowMoreRatios(!showMoreRatios)}
        />
        <VariantSection value={variantCount} onChange={setVariantCount} />
      </div>

      {/* Section 3: Style */}
      <StyleSection
        styleText={styleText}
        onStyleTextChange={setStyleText}
        selectedChips={selectedChips}
        onToggleChip={toggleChip}
        showAll={showAllChips}
        onToggleShowAll={() => setShowAllChips(!showAllChips)}
      />

      {/* Model selection */}
      <ModelSelector
        models={models}
        providers={providers}
        loading={modelsLoading}
        selectedModelId={selectedModelId}
        onModelChange={setSelectedModelId}
      />

      {/* Section 5: Reference Image (collapsible — only when selected model supports it) */}
      {supportsReferenceImage && (
        <Collapsible open={refOpen} onOpenChange={setRefOpen}>
          <CollapsibleTrigger className="flex items-center gap-2 w-full text-left text-sm font-medium hover:text-foreground/80">
            {refOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Reference image (style transfer)
            <Badge variant="outline" className="text-[10px] font-normal">
              optional
            </Badge>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 space-y-3">
            <ReferenceImageSection
              referencePreview={referencePreview}
              onUpload={handleReferenceUpload}
              onClear={clearReference}
              styleStrength={styleStrength}
              onStyleStrengthChange={setStyleStrength}
              contentMatch={contentMatch}
              onContentMatchChange={setContentMatch}
            />
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Section 6: Advanced Settings (collapsible) */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left text-sm font-medium hover:text-foreground/80">
          {advancedOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
          Advanced settings
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-4">
          <AdvancedSettingsSection
            negativePrompt={negativePrompt}
            onNegativePromptChange={setNegativePrompt}
            quality={quality}
            onQualityChange={setQuality}
            colorMood={colorMood}
            onColorMoodChange={setColorMood}
            seed={seed}
            onSeedChange={setSeed}
            rawMode={rawMode}
            onRawModeChange={setRawMode}
          />
        </CollapsibleContent>
      </Collapsible>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 mt-auto pt-2 border-t">
        <Button onClick={handleGenerate} disabled={!prompt.trim()} className="flex-1">
          <Sparkles className="h-4 w-4 mr-2" />
          Generate image{variantCount > 1 ? 's' : ''}
        </Button>
      </div>

      {/* History */}
      {history.length > 0 && <HistoryPanel history={history} onUse={onImageConfirmed} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 1: Prompt
// ─────────────────────────────────────────────────────────────────────────────

function PromptSection({
  prompt,
  onPromptChange,
  enhancedPrompt,
  showEnhanced,
  onToggleEnhanced,
  onEnhancedChange,
  onEnhance,
  isEnhancing,
}: {
  prompt: string;
  onPromptChange: (v: string) => void;
  enhancedPrompt: EnhancedPromptResult | null;
  showEnhanced: boolean;
  onToggleEnhanced: (v: boolean) => void;
  onEnhancedChange: (v: EnhancedPromptResult | null) => void;
  onEnhance: () => void;
  isEnhancing: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor="image-prompt">Describe your image</Label>
      <Textarea
        id="image-prompt"
        placeholder="e.g. A cozy coffee shop on a rainy afternoon, warm lighting, people reading..."
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        className="min-h-[80px] resize-none"
        maxLength={MAX_PROMPT_LENGTH}
      />
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs px-2 text-primary hover:text-primary/80"
          onClick={onEnhance}
          disabled={!prompt.trim() || isEnhancing}
        >
          {isEnhancing ? (
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3 mr-1" />
          )}
          Enhance prompt with AI
        </Button>
        <span className="text-xs text-muted-foreground">
          {prompt.length}/{MAX_PROMPT_LENGTH}
        </span>
      </div>

      {/* Enhanced prompt editor */}
      {enhancedPrompt && showEnhanced && (
        <div className="p-3 rounded-lg border bg-muted/30 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Enhanced prompt — review & edit before generating</p>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onToggleEnhanced(false)}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="space-y-1.5">
            {enhancedPrompt.fields.map((field, idx) => (
              <div key={field.label} className="flex items-start gap-2">
                <span className="text-xs font-medium text-muted-foreground min-w-[90px] pt-1.5">
                  {field.label}:
                </span>
                <Input
                  value={field.value}
                  onChange={(e) => {
                    const updated = [...enhancedPrompt.fields];
                    updated[idx] = { ...field, value: e.target.value };
                    onEnhancedChange({ ...enhancedPrompt, fields: updated });
                  }}
                  className="h-7 text-xs"
                />
                <button
                  type="button"
                  className="pt-1.5 text-muted-foreground hover:text-foreground flex-shrink-0"
                  onClick={() => {
                    const updated = enhancedPrompt.fields.filter((_, i) => i !== idx);
                    onEnhancedChange({ ...enhancedPrompt, fields: updated });
                  }}
                  title={`Remove ${field.label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          {enhancedPrompt.clarification && (
            <p className="text-[11px] text-muted-foreground italic">
              {enhancedPrompt.clarification}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2a: Aspect Ratio Grid
// ─────────────────────────────────────────────────────────────────────────────

/** Renders a proportional rectangle representing the aspect ratio */
function RatioVisual({ ratio }: { ratio: string }) {
  const [w, h] = ratio.split(':').map(Number);
  const maxDim = 28;
  const scale = maxDim / Math.max(w, h);
  const width = Math.round(w * scale);
  const height = Math.round(h * scale);
  return (
    <div
      className="rounded-sm bg-muted-foreground/20 border border-muted-foreground/30"
      style={{ width, height }}
    />
  );
}

function AspectRatioSection({
  value,
  onChange,
  showMore,
  onToggleMore,
}: {
  value: string;
  onChange: (v: string) => void;
  showMore: boolean;
  onToggleMore: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Aspect ratio</Label>
      <div className="grid grid-cols-4 gap-1.5">
        {PRIMARY_RATIOS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex flex-col items-center gap-1 p-2 rounded-md border text-[11px] transition-colors',
              value === opt.value
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-muted hover:border-muted-foreground/50 text-muted-foreground',
            )}
            title={opt.hint}
          >
            <RatioVisual ratio={opt.value} />
            <span>{opt.label}</span>
          </button>
        ))}
      </div>

      {SECONDARY_RATIOS.length > 0 && (
        <>
          <button
            type="button"
            className="text-[11px] text-primary hover:underline"
            onClick={onToggleMore}
          >
            {showMore ? 'Show less' : 'More ratios...'}
          </button>
          {showMore && (
            <div className="grid grid-cols-4 gap-1.5">
              {SECONDARY_RATIOS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange(opt.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 p-2 rounded-md border text-[11px] transition-colors',
                    value === opt.value
                      ? 'border-primary bg-primary/5 text-foreground'
                      : 'border-muted hover:border-muted-foreground/50 text-muted-foreground',
                  )}
                  title={opt.hint}
                >
                  <RatioVisual ratio={opt.value} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 2b: Variant Count
// ─────────────────────────────────────────────────────────────────────────────

function VariantSection({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Variants to generate</Label>
      <div className="flex gap-1.5">
        {VARIANT_OPTIONS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={cn(
              'flex-1 h-9 rounded-md border text-sm font-medium transition-colors',
              value === n
                ? 'border-primary bg-primary/5 text-foreground'
                : 'border-muted hover:border-muted-foreground/50 text-muted-foreground',
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">More variants = longer generation</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 3: Style
// ─────────────────────────────────────────────────────────────────────────────

function StyleSection({
  styleText,
  onStyleTextChange,
  selectedChips,
  onToggleChip,
  showAll,
  onToggleShowAll,
}: {
  styleText: string;
  onStyleTextChange: (v: string) => void;
  selectedChips: Set<string>;
  onToggleChip: (chip: string) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
}) {
  // Show first 2 categories by default, all when expanded
  const visibleCategories = showAll ? STYLE_CHIP_CATEGORIES : STYLE_CHIP_CATEGORIES.slice(0, 2);

  return (
    <div className="space-y-2">
      <Label className="text-xs">Style</Label>
      <Input
        value={styleText}
        onChange={(e) => onStyleTextChange(e.target.value)}
        placeholder="e.g. cinematic, watercolor, neon noir, minimalist flat..."
        className="h-8 text-sm"
      />

      <div className="space-y-2">
        {visibleCategories.map((cat) => (
          <div key={cat.label}>
            <p className="text-[10px] text-muted-foreground font-medium mb-1">{cat.label}</p>
            <div className="flex flex-wrap gap-1">
              {cat.chips.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => onToggleChip(chip)}
                  className={cn(
                    'px-2 py-0.5 rounded-full text-[11px] border transition-colors',
                    selectedChips.has(chip)
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/50',
                  )}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {STYLE_CHIP_CATEGORIES.length > 2 && (
        <button
          type="button"
          className="text-[11px] text-primary hover:underline"
          onClick={onToggleShowAll}
        >
          {showAll
            ? 'Show fewer styles'
            : `Show all styles (${STYLE_CHIP_CATEGORIES.length - 2} more categories)`}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Model Selector
// ─────────────────────────────────────────────────────────────────────────────

function ModelSelector({
  models,
  providers,
  loading,
  selectedModelId,
  onModelChange,
}: {
  models: ImageModel[];
  providers: ImageProviderInfo[];
  loading: boolean;
  selectedModelId: string;
  onModelChange: (id: string) => void;
}) {
  if (loading) {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">Model</Label>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading models...
        </div>
      </div>
    );
  }

  if (models.length === 0) return null;

  // Group models by provider for the dropdown
  const modelsByProvider = providers.reduce<Record<string, ImageModel[]>>((acc, prov) => {
    acc[prov.displayName] = models.filter((m) => m.provider === prov.key);
    return acc;
  }, {});

  // Find the recommended model for display
  const recommendedId = providers[0]?.recommendedModelId;

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">Model</Label>
      <Select value={selectedModelId} onValueChange={onModelChange}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Auto-select (recommended)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">
            <span className="text-muted-foreground">Auto-select (recommended)</span>
          </SelectItem>
          {Object.entries(modelsByProvider).map(([providerName, provModels]) =>
            provModels.length > 0 ? (
              <div key={providerName}>
                <div className="px-2 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  {providerName}
                </div>
                {provModels.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    <div className="flex items-center gap-2">
                      <span>{model.name}</span>
                      {model.id === recommendedId && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          rec
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </div>
            ) : null,
          )}
        </SelectContent>
      </Select>
      <p className="text-[10px] text-muted-foreground">
        Leave on auto to let the backend pick the best model for your style and settings
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 5: Reference Image
// ─────────────────────────────────────────────────────────────────────────────

function ReferenceImageSection({
  referencePreview,
  onUpload,
  onClear,
  styleStrength,
  onStyleStrengthChange,
  contentMatch,
  onContentMatchChange,
}: {
  referencePreview: string | null;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  styleStrength: number;
  onStyleStrengthChange: (v: number) => void;
  contentMatch: number;
  onContentMatchChange: (v: number) => void;
}) {
  return (
    <div className="space-y-3">
      {referencePreview ? (
        <div className="flex items-start gap-3">
          <div className="relative w-20 h-20 rounded-md overflow-hidden border flex-shrink-0">
            <img src={referencePreview} alt="Reference" className="w-full h-full object-cover" />
            <button
              type="button"
              className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-background/80 flex items-center justify-center text-muted-foreground hover:text-foreground"
              onClick={onClear}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="flex-1 space-y-3">
            <SliderField
              label="Style strength"
              value={styleStrength}
              onChange={onStyleStrengthChange}
            />
            <SliderField
              label="Content match"
              value={contentMatch}
              onChange={onContentMatchChange}
            />
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer hover:border-muted-foreground/50 transition-colors">
          <Upload className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Upload reference image</span>
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      )}
    </div>
  );
}

function SliderField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className="text-[11px] text-muted-foreground">{value}%</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={0}
        max={100}
        step={5}
        className="w-full"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section 6: Advanced Settings
// ─────────────────────────────────────────────────────────────────────────────

function AdvancedSettingsSection({
  negativePrompt,
  onNegativePromptChange,
  quality,
  onQualityChange,
  colorMood,
  onColorMoodChange,
  seed,
  onSeedChange,
  rawMode,
  onRawModeChange,
}: {
  negativePrompt: string;
  onNegativePromptChange: (v: string) => void;
  quality: string;
  onQualityChange: (v: string) => void;
  colorMood: string;
  onColorMoodChange: (v: string) => void;
  seed: string;
  onSeedChange: (v: string) => void;
  rawMode: boolean;
  onRawModeChange: (v: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Left column: Negative prompt */}
        <div className="space-y-1.5">
          <Label className="text-xs">Negative prompt</Label>
          <Textarea
            value={negativePrompt}
            onChange={(e) => onNegativePromptChange(e.target.value)}
            placeholder="Things to avoid: blurry, watermark, extra limbs, text..."
            className="min-h-[60px] resize-none text-xs"
            rows={2}
          />
        </div>

        {/* Right column: Quality + Color mood */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Quality</Label>
            <Select value={quality} onValueChange={onQualityChange}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUALITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Color mood</Label>
            <Select value={colorMood} onValueChange={onColorMoodChange}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLOR_MOOD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Seed */}
      <div className="space-y-1.5">
        <Label className="text-xs">Seed (optional)</Label>
        <Input
          type="number"
          value={seed}
          onChange={(e) => onSeedChange(e.target.value)}
          placeholder="Leave blank for random"
          className="h-8 text-xs"
          min={0}
          max={4294967295}
        />
        <p className="text-[10px] text-muted-foreground">
          Set a seed to reproduce a specific result
        </p>
      </div>

      {/* Raw mode */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="text-xs">Raw mode</Label>
          <p className="text-[10px] text-muted-foreground">Bypass style enhancements</p>
        </div>
        <Switch checked={rawMode} onCheckedChange={onRawModeChange} />
      </div>

      <p className="text-[10px] text-muted-foreground italic">
        Some settings apply only to specific models. The backend will use them when applicable.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// History panel
// ─────────────────────────────────────────────────────────────────────────────

function HistoryPanel({
  history,
  onUse,
  currentRequestId,
}: {
  history: GeneratedImageResult[];
  onUse: (img: GeneratedImageResult) => void;
  currentRequestId?: string;
}) {
  return (
    <div className="border-t pt-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        Previous generations ({history.length})
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {history.map((img) => (
          <button
            key={img.requestId}
            type="button"
            onClick={() => onUse(img)}
            className={cn(
              'relative flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors',
              img.requestId === currentRequestId
                ? 'border-primary'
                : 'border-transparent hover:border-muted-foreground/50',
            )}
            title={`Use: ${img.prompt}`}
          >
            <img src={img.imageUrl} alt={img.prompt} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}
